import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, XCircle, Palette } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2500;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      // Use auth token if available, otherwise call without
      const headers = {};
      const token = localStorage.getItem('access_token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await axios.get(`${API}/subscription/status/${sid}`, { headers });
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(res.data);
        return;
      } else if (res.data.status === 'expired') {
        setStatus('error');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
      <Card className="w-full max-w-md rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-8 text-center space-y-6">
          <div className="flex justify-center mb-2">
            <div className="p-3 rounded-xl bg-blue-600">
              <Palette className="h-8 w-8 text-white" />
            </div>
          </div>

          {status === 'checking' && (
            <>
              <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto" />
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Processing your payment...
              </h2>
              <p className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>
                Please wait while we confirm your subscription.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Subscription Activated!
              </h2>
              <p className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>
                Welcome to Create(ED)! Your subscription is now active and you have full access to all features.
              </p>
              {paymentData && paymentData.amount_total > 0 && (
                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
                  <p>Amount: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}</p>
                </div>
              )}
              <Button
                className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium"
                onClick={() => navigate('/')}
                data-testid="go-to-dashboard-after-payment"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Something went wrong
              </h2>
              <p className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>
                We couldn't verify your payment. Don't worry — if you were charged, your subscription will activate shortly.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-lg"
                  onClick={() => navigate('/subscription')}
                  data-testid="retry-subscription"
                >
                  Try Again
                </Button>
                <Button
                  className="flex-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => navigate('/')}
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
